import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Product, products as fallbackProducts } from '@/data/products';
import { 
  Moon, Sun, Flower, Sparkles, 
  LucideIcon 
} from 'lucide-react';

// Inline SVG placeholder for missing images
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f0f0' width='400' height='400'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3EGeen afbeelding%3C/text%3E%3C/svg%3E";

// Helper to normalize arrays that may contain strings or objects
const normalizeStringArray = (arr: any[]): string[] =>
  arr.map((item) =>
    typeof item === 'string'
      ? item
      : (item && typeof item === 'object')
        ? String((item as any).name ?? (item as any).label ?? (item as any).text ?? '')
        : String(item)
  ).filter(s => s.trim().length > 0);

// Map icon string names to actual icon components
const iconMap: Record<string, LucideIcon> = {
  'Moon': Moon,
  'Sun': Sun,
  'Flower': Flower,
  'Sparkles': Sparkles,
};

// Helper to resolve image URLs safely with robust fallback logic
function resolveImage(dbUrl?: string | null, fbUrl?: string, productSlug?: string) {
  // If database URL exists and is valid (not NULL, not empty, not /assets/ path)
  if (dbUrl && dbUrl.trim() !== '' && !dbUrl.startsWith('/assets/') && !dbUrl.startsWith('assets/')) {
    return dbUrl; // Use database URL (http(s) or storage URL)
  }
  
  // Otherwise use fallback from code
  const result = fbUrl ?? PLACEHOLDER_IMAGE;
  if (result === PLACEHOLDER_IMAGE && productSlug) {
    console.warn(`[useProducts] ${productSlug}: Geen foto gevonden, placeholder gebruikt`);
  }
  return result;
}

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // If database is empty, use fallback
      if (!data || data.length === 0) {
        console.warn('No products in database, using fallback data');
        setProducts(fallbackProducts);
        setIsLoading(false);
        return;
      }

      // Transform database products to match Product interface with robust fallbacks
      const transformedProducts: Product[] = (data || []).map((dbProduct) => {
        // Find fallback product for content, images, and defaults
        const fallbackProduct = fallbackProducts.find(p => p.slug === dbProduct.slug);
        
        return {
          id: (dbProduct.slug || '').split('').reduce((acc: number, ch: string) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7),
          dbId: dbProduct.id,
          name: dbProduct.name,
          slug: dbProduct.slug,
          tagline: dbProduct.tagline?.trim() || fallbackProduct?.tagline || '',
          description: dbProduct.description?.trim() || fallbackProduct?.description || '',
          longDescription: dbProduct.long_description?.trim() || dbProduct.description?.trim() || fallbackProduct?.longDescription || '',
          price25g: dbProduct.price_25g != null ? `€${dbProduct.price_25g.toFixed(2)}` : (fallbackProduct?.price25g || '€0,00'),
          price50g: dbProduct.price_50g != null ? `€${dbProduct.price_50g.toFixed(2)}` : (fallbackProduct?.price50g || '€0,00'),
          bundlePrice: dbProduct.bundle_price != null ? `€${dbProduct.bundle_price.toFixed(2)}` : (fallbackProduct?.bundlePrice || ''),
          originalPrice: dbProduct.original_price != null ? `€${dbProduct.original_price.toFixed(2)}` : (fallbackProduct?.originalPrice || ''),
          accentColor: dbProduct.accent_color || fallbackProduct?.accentColor || 'bg-purple-200',
          borderColor: dbProduct.border_color || fallbackProduct?.borderColor || 'border-purple-300',
          glowColor: dbProduct.glow_color || fallbackProduct?.glowColor || 'shadow-[0_0_25px_rgb(217,198,228,0.4)]',
          icon: iconMap[dbProduct.icon || 'Moon'] || fallbackProduct?.icon || Moon,
          bullets: (Array.isArray(dbProduct.bullets) && dbProduct.bullets.length) 
            ? normalizeStringArray(dbProduct.bullets)
            : (fallbackProduct?.bullets || []),
          ingredients: (Array.isArray(dbProduct.ingredients) && dbProduct.ingredients.length)
            ? normalizeStringArray(dbProduct.ingredients)
            : (fallbackProduct?.ingredients || []),
          benefits: (Array.isArray(dbProduct.benefits) && dbProduct.benefits.length)
            ? normalizeStringArray(dbProduct.benefits)
            : (fallbackProduct?.benefits || []),
          productImage: resolveImage(dbProduct.product_image_url, fallbackProduct?.productImage, dbProduct.slug),
          lifestyleImage: resolveImage(dbProduct.lifestyle_image_url, fallbackProduct?.lifestyleImage, dbProduct.slug),
          detailImage: resolveImage(dbProduct.detail_image_url, fallbackProduct?.detailImage, dbProduct.slug),
          testimonials: (Array.isArray(dbProduct.testimonials) && dbProduct.testimonials.length)
            ? dbProduct.testimonials.map((t: any) => ({
                name: String(t.name || ''),
                text: String(t.text || ''),
                rating: Number(t.rating || 5)
              }))
            : (fallbackProduct?.testimonials || []),
          isPopular: dbProduct.is_popular ?? fallbackProduct?.isPopular ?? false,
          isBestDeal: dbProduct.is_best_deal ?? fallbackProduct?.isBestDeal ?? false,
          isAccessory: dbProduct.is_accessory ?? fallbackProduct?.isAccessory ?? false,
          usage: dbProduct.usage?.trim() || fallbackProduct?.usage,
          contentInfo: dbProduct.content_info?.trim() || fallbackProduct?.contentInfo,
          seoDescription: dbProduct.seo_description?.trim() || fallbackProduct?.seoDescription,
        };
      });

      console.debug('[useProducts] Geladen producten:', transformedProducts.map(p => ({ 
        slug: p.slug, 
        hasProductImage: p.productImage !== PLACEHOLDER_IMAGE,
        hasLifestyleImage: p.lifestyleImage !== PLACEHOLDER_IMAGE,
        productImageUrl: p.productImage.substring(0, 50) 
      })));
      
      // Waarschuwing voor producten zonder foto's
      const productsWithoutImages = transformedProducts.filter(p => 
        p.productImage === PLACEHOLDER_IMAGE || p.lifestyleImage === PLACEHOLDER_IMAGE
      );
      if (productsWithoutImages.length > 0) {
        console.warn('[useProducts] Producten zonder foto\'s:', productsWithoutImages.map(p => p.slug));
      }
      
      setProducts(transformedProducts);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Error fetching products:', err);
      // On error, use fallback
      setProducts(fallbackProducts);
      setError(err.message);
      setIsLoading(false);
    }
  };

  return { products, isLoading, error, refetch: fetchProducts };
};
